import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getCauzioniRequest, getCauzioniRequestBackoffice } from '../../services/axios-client/axeCauzioni';
import { apiDownloadDocument, deleteDocument, uploadDocument } from '../../services/axios-client/axeCommons';
import { Uploader } from '../../common/components/redesignComponents/Uploader/Uploader';
import PageLoading from '../../common/componentsV2/elements/PageLoading';
import { ModificaDatiButton } from '../../common/components/redesignComponents/common/Redesign.styles';
import editIcon from '../../assets/redesign_icons/edit-icon.svg';
import { generalAlertError, generalAlertSuccessToast } from '../../common/utils/alerts';

const statusMessages = {
  pending: 'Richiesta inviata. In attesa di lavorazione.',
  accepted: 'La richiesta è stata accettata.',
  refused: 'La richiesta è stata rifiutata.',
};

const GREENIA_OUTBOUND_PHONE = '0442081682';

const statusAlertClass = {
  pending: 'alert-info',
  accepted: 'alert-success',
  refused: 'alert-danger',
};

const readAsBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result.replace(`data:${file.type};base64,`, ''));
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const formatDate = (value) => {
  if (!value) return '';
  const [y, m, d] = String(value).slice(0, 10).split('-');
  return d && m && y ? `${d}/${m}/${y}` : String(value);
};

const formatAmount = (value) => {
  if (value === null || value === undefined || value === '') return '';
  return `${Number(value).toLocaleString('it-IT', { minimumFractionDigits: 2 })} €`;
};

function SummaryRow({ label, children }) {
  return (
    <div className="cauzioni-summary-row">
      <span className="cauzioni-summary-label">{label}</span>
      <span className="cauzioni-summary-value">{children || '—'}</span>
    </div>
  );
}

/* One card, several sections — the shape of "Informazioni auto" on the other result pages,
   rather than a stack of separate cards per topic. */
function SummarySection({
  title, children, action, wide = false,
}) {
  return (
    <div className={`cauzioni-summary-section${wide ? ' cauzioni-summary-section-wide' : ''}`}>
      <div className="cauzioni-summary-section-head">
        <h3 className="cauzioni-summary-section-title">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

/* Terminal screen of the request flow. Product 1007 produces no quotations, so this is where a
   broker confirms what was actually filed and whether anything is still missing.

   The document list is not decoration. Uploads happen immediately after the request is created,
   and the upload endpoint forwards to a webservice whose reply it does not inspect — so a file
   can fail to store while the wizard reports success. Showing uploaded-vs-missing here, with an
   uploader on the missing ones, is what lets a broker notice and fix that themselves instead of
   the request sitting incomplete until someone phones the backoffice. */
function CauzioniResultPage({ backoffice = false }) {
  const { requestToken } = useParams();
  // Same screen for both audiences; only the token it authenticates with differs.
  const readRequest = backoffice ? getCauzioniRequestBackoffice : getCauzioniRequest;
  const navigate = useNavigate();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);

  const fetchDetail = () => readRequest(requestToken)
    .then((res) => { setDetail(res); setError(false); return res; })
    .catch(() => { setError(true); return null; });

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    readRequest(requestToken)
      .then((res) => { if (!cancelled) setDetail(res); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [requestToken]);

  /* Mirrors the download used by every other product's document list
     (PurchaseCondominioRequest.jsx:138-145): the endpoint returns the file inline as
     contents + filename, so the anchor is created and clicked rather than navigated to. */
  const handleDownload = (documentId) => {
    apiDownloadDocument(documentId)
      .then((response) => {
        const payload = response?.data?.data;
        if (response.status !== 200 || !payload?.contents) {
          generalAlertError('Impossibile scaricare il documento. Riprova più tardi.');
          return;
        }
        const link = document.createElement('a');
        link.target = '_blank';
        link.href = payload.contents;
        link.download = payload.filename;
        link.click();
      })
      .catch(() => generalAlertError('Impossibile scaricare il documento. Riprova più tardi.'));
  };

  const removeDocument = (documentId) => {
    deleteDocument(documentId).then(() => fetchDetail());
  };

  const MAX_FILE_SIZE_MB = 10;

  const handleUpload = (documentTypeId, file) => {
    if (!detail?.id || !file) return;

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      generalAlertError(`Il file supera il limite di ${MAX_FILE_SIZE_MB} MB.`);
      return;
    }

    setUploadingId(documentTypeId);

    readAsBase64(file)
      .then((unsignedDocument) => uploadDocument({
        document_type_id: documentTypeId,
        request_id: detail.id,
        original_filename: file.name,
        unsigned_document: unsignedDocument,
      }))
      .then((response) => {
        if (response.status !== 200) {
          generalAlertError('Impossibile caricare il documento. Riprova più tardi.');
          return null;
        }
        // That 200 is not proof: the endpoint answers success even when the documents
        // webservice rejected the file. Confirm against what the request actually holds now.
        return fetchDetail().then((fresh) => {
          const landed = (fresh?.documents?.uploaded || [])
            .some((u) => u.document_type_id === documentTypeId);
          if (fresh && !landed) {
            generalAlertError('Il documento non risulta caricato. Riprova.');
            return;
          }
          generalAlertSuccessToast('File caricato');
        });
      })
      .catch(() => generalAlertError('Impossibile caricare il documento. Riprova più tardi.'))
      .finally(() => setUploadingId(null));
  };

  // The platform's loading screen, same as every other result page.
  if (loading) {
    return <PageLoading />;
  }

  if (error || !detail) {
    return (
      <div className={`cauzioni-page${backoffice ? ' cauzioni-page-backoffice' : ''}`}>
        <p className="cauzioni-count cauzioni-error">
          Impossibile caricare la richiesta. Riprova più tardi.
        </p>
      </div>
    );
  }

  const message = statusMessages[detail.status]
    || 'Impossibile caricare la richiesta. Riprova più tardi.';
  const alertClass = statusAlertClass[detail.status] || 'alert-secondary';

  const contractor = detail.contractor || {};
  const beneficiary = detail.beneficiary || {};
  const guarantee = detail.guarantee || {};
  const contact = detail.contact || {};

  const contractorName = contractor.gender === 'G'
    ? contractor.business_name
    : [contractor.name, contractor.surname].filter(Boolean).join(' ');
  const contractorCode = contractor.gender === 'G'
    ? contractor.vat_number && `P.IVA ${contractor.vat_number}`
    : contractor.fiscal_code;

  const allDocs = [
    ...(detail.documents?.required || []),
    ...(detail.documents?.on_request || []),
  ];
  const uploadedByType = new Map(
    (detail.documents?.uploaded || []).map((u) => [u.document_type_id, u]),
  );
  const uploadedCount = allDocs.filter((d) => uploadedByType.has(d.document_type_id)).length;
  const missingCount = allDocs.length - uploadedCount;

  const downloadableDocs = detail.risk?.downloadable_documents || [];
  const questionnaireByDocTypeId = new Map(
    downloadableDocs
      .filter((d) => d.type === 'questionario' && d.document_type_id)
      .map((d) => [d.document_type_id, d]),
  );
  const infoDocs = downloadableDocs.filter((d) => d.type !== 'questionario');

  return (
    <div
      className={`cauzioni-page${backoffice ? ' cauzioni-page-backoffice' : ''}`}
      data-testid="cauzioni-result"
      data-status={detail.status}
    >
      {detail.status === 'accepted' ? (
        <>
          <p className="cauzioni-accepted-text">{message}</p>
          <div className="cauzioni-accepted-card">
            <h2 className="cauzioni-card-title mb-3">Dettagli accettazione</h2>

            {detail.bozza && (
              <p className="mb-2">
                Ecco la bozza della garanzia:
                {' '}
                <button
                  type="button"
                  className="cauzioni-link-btn"
                  onClick={() => handleDownload(detail.bozza.document_id)}
                >
                  {detail.bozza.filename || 'Bozza.pdf'}
                </button>
              </p>
            )}

            {detail.quotation?.amount != null && (
              <p className="cauzioni-premio mb-2">
                Premio:
                {' '}
                <strong>
                  {`€ ${Number(detail.quotation.amount).toLocaleString('it-IT', { minimumFractionDigits: 2 })}`}
                </strong>
              </p>
            )}

            {contractor.phone && (
              <>
                <p className="mb-1">
                  {`Il nostro staff ti ricontatterà telefonicamente al numero ${contractor.phone}, chiamando dal ${GREENIA_OUTBOUND_PHONE}, per concordare un appuntamento in agenzia.`}
                </p>
                <p className="mb-0">
                  Durante l&apos;appuntamento procederemo con la firma della documentazione e il pagamento del premio.
                </p>
              </>
            )}

            {!backoffice && detail.request_token && (
              <div className="cauzioni-proceed-row">
                <button
                  type="button"
                  className="btn btn-questionnaire cauzioni-proceed-btn"
                  onClick={() => navigate(`/quotes/offline/purchase/${detail.request_token}`)}
                >
                  Procedi con il pagamento
                </button>
              </div>
            )}
          </div>

          {(() => {
            const polizza = (detail.documents?.uploaded || []).find(
              (u) => u.document_type_id === 35 || u.document_type_id === 13,
            );
            if (!polizza) return null;
            const q = detail.quotation;
            return (
              <div className="cauzioni-accepted-card">
                <h2 className="cauzioni-card-title mb-3">Polizza emessa</h2>
                {q?.amount != null && (
                  <p className="cauzioni-premio mb-2">
                    Premio:
                    {' '}
                    <strong>
                      {`€ ${Number(q.amount).toLocaleString('it-IT', { minimumFractionDigits: 2 })}`}
                    </strong>
                  </p>
                )}
                {q?.emission_date && (
                  <p className="mb-2">
                    Data emissione:
                    {' '}
                    <strong>{new Date(q.emission_date).toLocaleDateString('it-IT')}</strong>
                  </p>
                )}
                {q?.saved_code_id && (
                  <p className="mb-2">
                    Numero Polizza:
                    {' '}
                    <strong>{q.saved_code_id}</strong>
                  </p>
                )}
                <p className="mb-0">
                  <button
                    type="button"
                    className="cauzioni-link-btn"
                    onClick={() => handleDownload(polizza.document_id)}
                  >
                    Scarica Polizza
                  </button>
                </p>
              </div>
            );
          })()}
        </>
      ) : detail.status === 'refused' ? (
        <>
          <p className="cauzioni-refused-text">{message}</p>
          {detail.status_reason && (
            <div className="cauzioni-refused-card">
              <p className="mb-0">
                <strong>Motivazione:</strong>
                {' '}
                {detail.status_reason}
              </p>
            </div>
          )}
        </>
      ) : (
        <div className={`alert ${alertClass} mt-3`} role="alert">
          {message}
        </div>
      )}

      <div className="cauzioni-info-card">
        <div className="cauzioni-card-body">
          <div className="cauzioni-summary-head">
            <h2 className="cauzioni-card-title mb-0">
              Informazioni
              {' '}
              <strong>richiesta</strong>
            </h2>
            {/* Reopens the wizard prefilled with this request. Editing never mutates the
                original — submitting creates a NEW request, exactly as every other product's
                Modifica Dati does (RcFamigliaResultPage.jsx:39-43).

                Each audience has to stay inside its own session: the broker route is behind
                broker auth, so sending a backoffice user there logged them out to the broker
                login page instead of opening the form. */}
            <ModificaDatiButton
              onClick={() => navigate(
                backoffice
                  ? `/backoffice/quote/${requestToken}/surety-bonds`
                  : `/quote/surety-bonds/${detail.sub_product.slug}/request?risk=${detail.risk.id}`,
                backoffice ? undefined : { state: { requestToken } },
              )}
              data-testid="cauzioni-modify-data"
            >
              <p>Modifica Dati</p>
              <img src={editIcon} alt="Modifica Dati" />
            </ModificaDatiButton>
          </div>

          {/* Two columns of sections on wide screens — Prodotto beside Contraente, and so on —
              so the card reads as a compact block instead of one very long single column. */}
          <div className="cauzioni-summary-grid">
            {/* Spans both columns: two rows of data beside Contraente's seven left a tall void,
                because a grid row is as tall as its tallest cell. The remaining sections are
                paired by similar height so the columns stay level. */}
            <SummarySection title="Prodotto" wide>
              <SummaryRow label="Prodotto">{detail.sub_product.name}</SummaryRow>
              <SummaryRow label="Garanzia">{detail.risk.name}</SummaryRow>
            </SummarySection>

            <SummarySection title="Contraente">
              <SummaryRow label="Denominazione">{contractorName}</SummaryRow>
              <SummaryRow label={contractor.gender === 'G' ? 'Partita IVA' : 'Codice fiscale'}>
                {contractorCode}
              </SummaryRow>
              <SummaryRow label="Indirizzo">{contractor.address}</SummaryRow>
              <SummaryRow label="Comune">
                {contractor.commune
            && `${contractor.commune}${contractor.province ? ` (${contractor.province})` : ''}${contractor.postal_code ? ` · ${contractor.postal_code}` : ''}`}
              </SummaryRow>
              <SummaryRow label="Email">{contractor.email}</SummaryRow>
              <SummaryRow label="PEC">{contractor.pec}</SummaryRow>
              <SummaryRow label="Telefono">{contractor.phone}</SummaryRow>
            </SummarySection>

            <SummarySection title="Beneficiario">
              <SummaryRow label="Denominazione">{beneficiary.business_name}</SummaryRow>
              <SummaryRow label="P.IVA / C.F.">{beneficiary.vat_or_fiscal_code}</SummaryRow>
            </SummarySection>

            <SummarySection title="Dati della garanzia">
              <SummaryRow label="Decorrenza">{formatDate(guarantee.effective_date)}</SummaryRow>
              <SummaryRow label="Scadenza">{formatDate(guarantee.expiry_date)}</SummaryRow>
              <SummaryRow label="Somma garantita">{formatAmount(guarantee.guaranteed_amount)}</SummaryRow>
              <SummaryRow label="Descrizione">{guarantee.description}</SummaryRow>
              <SummaryRow label="Note">{guarantee.notes}</SummaryRow>
            </SummarySection>

            {(contact.name || contact.phone || contact.email) && (
              <SummarySection title="Referente aziendale">
                <SummaryRow label="Nome">{contact.name}</SummaryRow>
                <SummaryRow label="Telefono">{contact.phone}</SummaryRow>
                <SummaryRow label="Email">{contact.email}</SummaryRow>
              </SummarySection>
            )}
          </div>
        </div>
      </div>

      <div className="cauzioni-info-card">
        <div className="cauzioni-card-body">
          <div className="cauzioni-summary-head">
            <h2 className="cauzioni-card-title mb-0">Documenti</h2>
            <span className="cauzioni-count mb-0" data-testid="cauzioni-result-doc-count">
              {`${uploadedCount} di ${allDocs.length}`}
            </span>
          </div>
          {missingCount > 0 && (
            <p className="cauzioni-count cauzioni-error" data-testid="cauzioni-result-missing">
              {missingCount === 1
                ? 'Manca 1 documento. Caricalo per completare la richiesta.'
                : `Mancano ${missingCount} documenti. Caricali per completare la richiesta.`}
            </p>
          )}

          <div className="cauzioni-doc-list">
            {allDocs.map((doc) => {
              const uploaded = uploadedByType.get(doc.document_type_id);
              const template = questionnaireByDocTypeId.get(doc.document_type_id);

              if (!uploaded) {
                return (
                  <div
                    className="cauzioni-doc-row cauzioni-doc-row-missing"
                    key={doc.document_type_id}
                    data-testid="cauzioni-result-doc"
                    data-uploaded="false"
                  >
                    <span className="cauzioni-doc-row-badge cauzioni-doc-row-badge-missing">Mancante</span>
                    <span className="cauzioni-doc-row-name">
                      {doc.name}
                      {template && (
                        <a
                          className="cauzioni-doc-row-template"
                          href={`${process.env.REACT_APP_BACKEND_API_URL}/surety-bonds/downloadable-document/${encodeURIComponent(template.filename)}`}
                        >
                          Scarica modulo da compilare
                        </a>
                      )}
                    </span>
                    <div className="cauzioni-doc-row-upload">
                      <Uploader
                        onFileUpload={(file) => handleUpload(doc.document_type_id, file)}
                        note={uploadingId === doc.document_type_id
                          ? 'Caricamento...'
                          : 'Carica'}
                      />
                    </div>
                  </div>
                );
              }

              return (
                <div
                  className="cauzioni-doc-row"
                  key={doc.document_type_id}
                  data-testid="cauzioni-result-doc"
                  data-uploaded="true"
                >
                  {uploaded.is_uploaded_valid === 1 && (
                    <span className="cauzioni-doc-row-badge cauzioni-doc-row-badge-valid">Valido</span>
                  )}
                  {uploaded.is_uploaded_valid === 0 && (
                    <span className="cauzioni-doc-row-badge cauzioni-doc-row-badge-invalid">Non Valido</span>
                  )}
                  {uploaded.is_uploaded_valid === null && (
                    <span className="cauzioni-doc-row-badge cauzioni-doc-row-badge-pending">In verifica</span>
                  )}
                  <span className="cauzioni-doc-row-name">{doc.name}</span>
                  {uploaded.motivation && (
                    <span className="cauzioni-doc-row-motivation">{uploaded.motivation}</span>
                  )}
                  <div className="cauzioni-doc-row-actions">
                    <button
                      type="button"
                      className="cauzioni-download-btn"
                      onClick={() => handleDownload(uploaded.document_id)}
                      data-testid="cauzioni-result-doc-download"
                    >
                      Scarica
                    </button>
                    {uploaded.is_uploaded_valid === 0 && (
                      <button
                        type="button"
                        className="cauzioni-doc-row-dismiss"
                        onClick={() => removeDocument(uploaded.document_id)}
                        title="Elimina e ricarica"
                      >
                        Ricarica
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {infoDocs.length > 0 && (
            <div className="cauzioni-downloadable-docs" style={{ marginTop: 16 }}>
              <h3>Documenti informativi</h3>
              {infoDocs.map((doc) => (
                <div className="cauzioni-download-item" key={doc.filename}>
                  <span className={`cauzioni-download-badge cauzioni-download-badge-${doc.type === 'set_informativo' ? 'set-informativo' : 'pog'}`}>
                    {doc.type === 'set_informativo' ? 'SET INFORMATIVO' : 'POG'}
                  </span>
                  <span className="cauzioni-download-name">{doc.name}</span>
                  <a
                    className="cauzioni-download-btn"
                    href={`${process.env.REACT_APP_BACKEND_API_URL}/surety-bonds/downloadable-document/${encodeURIComponent(doc.filename)}`}
                  >
                    Scarica
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

    </div>
  );
}

export default CauzioniResultPage;
